import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { locationPropType } from './util/index';
import { isRadiusUnitMiles } from './util/radius-unit';
import { getLocationName } from './util/get-location-name';
/**
 * UI string that describes a location target.
 * @param {*} props - React props.
 * @returns {ReactNode} - Span element containing the appropriate string for the location target.
 */
function LocationTargetNameInternal(props) {
  const {
    isRadiusTarget,
    radiusValue,
    radiusUnit,
    location,
    instrumentationService,
    permissions,
  } = props;

  const i18n = useContext(I18nContext);
  const isMicrosoftBingDMAEnabled = _.result(permissions.dynamic, 'UpdateNielsenToMicrosoftBingDMA', false);

  if (isRadiusTarget) {
    let radiusUnitString = null;
    if (isRadiusUnitMiles(radiusUnit)) {
      radiusUnitString = i18n.getString(radiusValue === 1 ? _TL_('mile') : _TL_('miles'));
    } else {
      radiusUnitString = i18n.getString(radiusValue === 1 ? _TL_('kilometer') : _TL_('kilometers'));
    }

    // e.g. "25 miles around Seattle, Washington, United States"
    const radiusLocationTarget = i18n.getString(_TL_('{{distance}} {{unit}} around {{name}}'), {
      name: getLocationName({ location, isRadiusTarget, instrumentationService }),
      distance: radiusValue,
      unit: radiusUnitString,
    });

    return (
      <span
        className="radius-location-target"
        data-clarity-unmask="true"
      >
        {radiusLocationTarget}
      </span>
    );
  }

  const locationDMAString = isMicrosoftBingDMAEnabled ? _TL_('(Microsoft Market Area)') : _TL_('(Nielsen DMA®)');

  let locationTypeKey = null;
  switch (location.Type) {
    case 'Country':
      locationTypeKey = location.CountryCode === 'TW' ? '' : _TL_('(country/region)');
      break;
    case 'City':
      locationTypeKey = _TL_('(city)');
      break;
    case 'PopulatedPlace':
      locationTypeKey = _TL_('(city)');
      break;
    case 'MetroArea':
      locationTypeKey = location.CountryCode === 'US' ? locationDMAString : _TL_('(metro area)');
      break;
    case 'SubGeography':
      locationTypeKey = location.CountryCode === 'TW' ? '' : _TL_('(state/province)');
      break;
    case 'PostalCode':
      locationTypeKey = _TL_('(ZIP code)');
      break;
    case 'County':
      locationTypeKey = location.CountryCode === 'US' ? _TL_('(county)') : _TL_('(N/A)');
      break;
    case 'Neighborhood':
      locationTypeKey = _TL_('(neighborhood)');
      break;
    default:
      locationTypeKey = _TL_('(N/A)');
  }

  // e.g. "Australia (country/region)" or "SEATTLE-TACOMA, Washington, United States (Nielsen DMA®)"
  const dispalyText = `${getLocationName({ location, isRadiusTarget, instrumentationService })} ${i18n.getString(locationTypeKey)}`;
  return (
    <span className="location-target" data-clarity-unmask="true">
      {dispalyText.trimEnd()}
    </span>
  );
}

LocationTargetNameInternal.propTypes = {
  isRadiusTarget: PropTypes.bool.isRequired,
  location: locationPropType.isRequired,
  radiusValue: PropTypes.number,
  radiusUnit: PropTypes.oneOf(['Miles', 'miles', 'mi', 'Kilometers', 'kilometers', 'km']),
  instrumentationService: PropTypes.shape({
    logError: PropTypes.func.isRequired,
    logTrace: PropTypes.func.isRequired,
  }),
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({}),
  }).isRequired,
};

LocationTargetNameInternal.defaultProps = {
  radiusValue: null,
  radiusUnit: null,
  instrumentationService: { logError: _.noop, logTrace: _.noop },
};

export const LocationTargetName = _.compose(withPermissions)(LocationTargetNameInternal);
